<template>
  <div class="clip-art-component">
    <ToolTitle :title="title" />
    <div v-if="!currentActiveObject || currentActiveObject.name !== 'clipArt'">
      <form id="search" @submit.prevent="handleSubmit">
        <label for="search-input" id="img-wrap">
          <img src="@/assets/icons/searchGlass.svg" />
        </label>
        <input placeholder="Search for clipart" id="search-input" @input="handleInput" />
        <input style="display: none" type="submit" />
      </form>
      <div id="loading-icon">
        <svg
          v-if="loading"
          version="1.1"
          id="loader-1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          width="40px"
          height="40px"
          viewBox="0 0 50 50"
          style="enable-background: new 0 0 50 50"
          xml:space="preserve"
        >
          <path
            fill="#ff6c00"
            d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z"
          >
            <animateTransform
              attributeType="xml"
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="0.6s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
        <div v-else id="clear" @click="clearIcons">Clear</div>
      </div>
      <div v-if="!icons.length">
        <div id="categories">
          <h4>Popular Categories</h4>
          <div id="icons-wrap">
            <div
              class="icon"
              v-for="(art, idx) in clipArtImages"
              v-bind:key="idx"
              @click="handleClick(art.path)"
            >
              <img :src="require(`@/assets/icons/clipcats/${art.path}.svg`)" />
              <p>{{ art.name }}</p>
            </div>
          </div>
        </div>
      </div>
      <div v-else id="after-search">
        <div id="after-search-icons-wrap">
          <div
            class="after-search-icon"
            v-for="(icon, idx) in icons"
            v-bind:key="idx"
            @click="addToCanvas(icon.id)"
          >
            <img :src="icon.icon_url" />
          </div>
        </div>
      </div>
    </div>
    <Customize v-else />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import axios from "axios";

import ToolTitle from "./ToolTitle.vue";
import Customize from "./Customize.vue";
import JSON from "@/data/cliparts.json";
import { store } from "@/store";
import { makeNewClipArt } from "@/canvas-api/UserObjects/ClipArt";

interface ClipArts {
  path: string;
  name: string;
}

interface Icons {
  id: string;
  icon_url: string;
  is_active: string;
  attribution: string;
}

export default defineComponent({
  name: "ClipArt",
  components: {
    ToolTitle,
    Customize,
  },
  setup() {
    const title = ref("Clip Art");
    const clipArtImages = ref<ClipArts[]>(JSON.clipArts);
    const icons = ref<Icons[]>([]);

    const currentSearch = ref("");
    const loading = ref(false);

    const currentActiveObject = computed(() => store.getters.currentActiveObject);

    watch(currentActiveObject, (newObject) => {
      if (newObject) {
        title.value = "Customize";
      }
    });

    onMounted(() => {
      if (currentActiveObject.value && currentActiveObject.value.name === "clipArt") {
        title.value = "Customize";
      }
    });

    const handleInput = (e: { target: HTMLInputElement }) => {
      currentSearch.value = e.target.value;
    };

    const handleClick = async (search: string) => {
      loading.value = true;
      currentSearch.value = search;

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/editor/clipart/ajax.php?phrase=${currentSearch.value}`
        );

        icons.value = res.data.icons;
        loading.value = false;
      } catch (e) {
        loading.value = false;
        console.log(e);
      }
    };

    const handleSubmit = async () => {
      loading.value = true;

      if (currentSearch.value.length) {
        try {
          const res = await axios.get(
            `${process.env.VUE_APP_API_URL}/editor/clipart/ajax.php?phrase=${currentSearch.value}`
          );

          icons.value = res.data.icons;
          loading.value = false;
        } catch (e) {
          loading.value = false;
          console.log(e);
        }
      }
    };

    const clearIcons = () => {
      icons.value = [];
    };

    const addToCanvas = async (id: string) => {
      loading.value = true;

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/editor/clipart/ajax.php?icon_id=${id}`
        );

        makeNewClipArt(res.data);

        loading.value = false;
      } catch (e) {
        loading.value = false;
        console.log(e);
      }
    };

    return {
      title,
      clipArtImages,
      icons,
      currentSearch,
      loading,
      currentActiveObject,
      handleInput,
      handleClick,
      handleSubmit,
      clearIcons,
      addToCanvas,
    };
  },
});
</script>

<style>
#search {
  width: 315px;
  height: 60px;
  margin: 20px auto 0 auto;
  display: flex;
}

#img-wrap {
  width: 60px;
  height: 60px;
  background-color: #fff;
  border-bottom-left-radius: 4px;
  border-top-left-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
}

#img-wrap img {
  width: 50%;
  height: 50%;
}

#search-input {
  width: calc(100% - 60px);
  height: 100%;
  border-bottom-right-radius: 4px;
  border-top-right-radius: 4px;
  font-size: 16px;
  outline: none;
  border: 0;
  padding: 0;
}

#loading-icon,
#clear {
  margin: 15px auto;
  width: 50%;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

#clear {
  color: #fff;
  background: #ff6c00;
  font-size: 16px;
  border-radius: 4px;
}

#clear:hover {
  color: #ff6c00;
  background: #fff;
  transition: color 0.25s, background-color 0.5s ease;
}

#categories {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

#categories h4 {
  width: 210px;
  font-size: 2.3em;
  font-weight: 700;
  text-align: center;
  color: #fff !important;
}

#icons-wrap {
  width: 250px;
  height: 375px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

.icon {
  background: #fff;
  width: 85px;
  height: 85px;
  margin: 20px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  border: 2px solid transparent;
}

.icon:hover {
  border: 2px solid #ff6c00;
  transition: border 0.25s ease;
}

.icon p {
  color: #000;
  font-size: 12px;
}

#after-search {
  margin-top: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

#after-search-icons-wrap {
  height: calc(100vh - 235px);
  width: 250px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

.after-search-icon {
  width: 85px;
  height: 85px;
  margin: 20px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  border: 2px solid transparent;
}

.after-search-icon img {
  width: 100%;
  height: 100%;
}

.after-search-icon:hover {
  border: 2px solid #ff6c00;
  transition: border 0.25s ease;
}

#after-search::-webkit-scrollbar {
  width: 10px;
}

#after-search::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 20px;
}

#after-search::-webkit-scrollbar-track {
  background: #ddd;
  border-radius: 20px;
}

@media (max-width: 767px) {
  #search,
  #loading-icon {
    width: 60%;
  }

  #clear {
    width: 100%;
  }

  #icons-wrap {
    width: 60%;
    height: auto;
    justify-content: space-evenly;
    margin: 25px auto;
  }
}

@media (max-width: 490px) {
  #icons-wrap {
    width: 90%;
  }
}
</style>
